var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { refreshGrant } from '../keycloak-client/index.mjs';
import { systemClock } from '../clock.mjs';
import { isJWTExpired, isParsableJWT } from '../jwt.mjs';
/**
 * This class allows 3rd party apps (like Profolio) to authenticate with Keycloak through Strat
 */
export class KeycloakStratAuthenticator {
    get accessToken() {
        return this._accessToken;
    }
    get idToken() {
        return this._idToken;
    }
    get refreshToken() {
        return this._refreshToken;
    }
    constructor(keycloakUrl, realm, clientID, stratDomainUrl) {
        this._validLanguageCodes = ['en', 'ar'];
        this._keycloakConfig = Object.freeze({
            realm: realm,
            url: keycloakUrl,
            clientID: clientID,
            timeout: 10000,
            clock: systemClock(),
        });
        this._stratDomainUrl = stratDomainUrl;
        this._accessToken = null;
        this._idToken = null;
        this._refreshToken = null;
    }
    /**
     * Checks if user is authenticated based on the Keycloak access, id and
     * refresh token cookies.
     *
     * If refresh token is missing, expired or invalid, the user is not
     * authenticated.
     *
     * If access or id tokens are missing, expired or invalid, this method
     * attempts to refresh the session and update the access and id token cookies.
     *
     * The method also updates the access, id and refresh token properties
     * based on the values of the cookies.
     *
     * @returns Boolean indicating whether user is authenticated
     */
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.requestStratKeycloakSession();
            if (!response || response.status !== 200) {
                return false;
            }
            const tokenData = yield response.json();
            this._accessToken = tokenData.accessToken;
            this._idToken = tokenData.idToken;
            this._refreshToken = tokenData.refreshToken;
            if (!this._accessToken || !this._idToken) {
                yield this.refresh(this._refreshToken);
            }
            return !!this.accessToken && !!this.idToken && !!this.refreshToken;
        });
    }
    /**
     * Redirects to Keycloak login page served by Strat.
     */
    login() {
        const languageCode = window.location.pathname.substring(1).split('/')[0];
        const languageCodePath = this._validLanguageCodes.includes(languageCode)
            ? `/${languageCode}`
            : '';
        const stratLoginPath = `/account?externalRedirectPath=${window.location.href}`;
        const loginPageUrl = `${this._stratDomainUrl}${languageCodePath}${stratLoginPath}`;
        window.location.replace(loginPageUrl);
    }
    /**
     * Deletes Keycloak access, id and refresh token cookies.
     */
    logout() {
        this._accessToken = null;
        this._idToken = null;
        this._refreshToken = null;
        this.deleteStratKeycloakSession();
    }
    /**
     * Refreshes Keycloak session using the refresh token passed as argument.
     * If refresh was successful, it updates the Keycloak access, id and refresh token cookies
     * as well as the properties.
     *
     * @param refreshToken Keycloak refresh token
     * @returns An object with keys: accessToken, idToken, refreshToken
     */
    refresh(refreshToken) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!refreshToken ||
                !isParsableJWT(refreshToken) ||
                isJWTExpired(this._keycloakConfig.clock, refreshToken)) {
                return;
            }
            const tokenData = yield refreshGrant(this._keycloakConfig, refreshToken);
            yield this.postStratKeycloakSession({
                accessToken: tokenData.accessToken,
                idToken: tokenData.idToken,
                refreshToken: tokenData.refreshToken,
            });
            this._accessToken = tokenData.accessToken;
            this._idToken = tokenData.idToken;
            this._refreshToken = tokenData.refreshToken;
        });
    }
    deleteStratKeycloakSession() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.requestStratKeycloakSession({ method: 'DELETE' });
        });
    }
    postStratKeycloakSession(data) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.requestStratKeycloakSession({
                method: 'POST',
                body: JSON.stringify(data),
            });
        });
    }
    requestStratKeycloakSession() {
        return __awaiter(this, arguments, void 0, function* (options = {}) {
            try {
                const finalOptions = Object.assign({ headers: {
                        'Content-Type': 'application/json',
                    }, mode: 'cors', credentials: 'include' }, options);
                return yield fetch(`${this._stratDomainUrl}/api/keycloak/session`, finalOptions);
            }
            catch (error) {
                throw new Error(`Request to ${this._stratDomainUrl} failed: ${error}`);
            }
        });
    }
}
